import React, { useState } from 'react';

const nodeTypes = [
  { value: 'rectangle', label: 'Rectangle (Process)', icon: '▭' },
  { value: 'rounded', label: 'Rounded (Terminal)', icon: '⬭' },
  { value: 'diamond', label: 'Diamond (Decision)', icon: '◇' },
  { value: 'parallelogram', label: 'Parallelogram (I/O)', icon: '▱' }
];

const colorPresets = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#6b7280', label: 'Gray' },
  { value: '#06b6d4', label: 'Cyan' }
];

const BulkEditModal = ({ isOpen, selectedNodes, onClose, onSave }) => {
  const [changeType, setChangeType] = useState(false);
  const [changeColor, setChangeColor] = useState(false);
  const [changeSize, setChangeSize] = useState(false);
  const [type, setType] = useState('rectangle');
  const [color, setColor] = useState('#3b82f6');
  const [width, setWidth] = useState(140);
  const [height, setHeight] = useState(60);

  if (!isOpen || !selectedNodes || selectedNodes.length === 0) return null;

  const handleSave = () => {
    const changes = {};
    if (changeType) changes.type = type;
    if (changeColor) changes.color = color;
    if (changeSize) {
      changes.width = parseInt(width);
      changes.height = parseInt(height);
    }

    if (Object.keys(changes).length > 0) {
      onSave(selectedNodes.map(node => node.id), changes);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit {selectedNodes.length} Nodes
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select which properties to change for all {selectedNodes.length} selected nodes.
          </p>

          {/* Change Shape */}
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={changeType}
                onChange={(e) => setChangeType(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Change Shape
              </span>
            </label>
            {changeType && (
              <div className="grid grid-cols-2 gap-2 ml-6">
                {nodeTypes.map((nodeType) => (
                  <button
                    key={nodeType.value}
                    onClick={() => setType(nodeType.value)}
                    className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg transition-all ${
                      type === nodeType.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-lg">{nodeType.icon}</span>
                    <span className={`text-sm ${
                      type === nodeType.value
                        ? 'text-blue-700 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {nodeType.label.split(' ')[0]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Change Color */}
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={changeColor}
                onChange={(e) => setChangeColor(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Change Color
              </span>
            </label>
            {changeColor && (
              <div className="flex flex-wrap gap-2 ml-6">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => setColor(preset.value)}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      color === preset.value
                        ? 'border-gray-900 dark:border-white ring-2 ring-offset-2 ring-blue-500'
                        : 'border-gray-200 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: preset.value }}
                    title={preset.label}
                  />
                ))}
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-600 cursor-pointer"
                  title="Custom color"
                />
              </div>
            )}
          </div>

          {/* Change Size */}
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={changeSize}
                onChange={(e) => setChangeSize(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Change Size
              </span>
            </label>
            {changeSize && (
              <div className="grid grid-cols-2 gap-4 ml-6">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Width
                  </label>
                  <input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    min={60}
                    max={300}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Height
                  </label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    min={30}
                    max={200}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!changeType && !changeColor && !changeSize}
            className={`px-4 py-2 rounded-lg transition-colors font-medium ${
              changeType || changeColor || changeSize
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Apply to {selectedNodes.length} Nodes
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
